import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  plaidService,
  ConnectionStatusResponse,
  ConnectionTargetStatus,
  PlaidAccount,
} from "../../services/external/plaidService";

interface ConnectChecklistProps {
  // Loaded accounts, used to resolve Plaid-bank connection state client-side
  // (the backend can't cheaply resolve per-institution Plaid identity).
  accounts: PlaidAccount[];
  onConnectBank: () => void;
  onConnectStripe: () => void;
  onAddWallet: (
    provider: "cash_app" | "venmo" | "lyft_direct" | "uber_card"
  ) => void;
  // Bumped by the parent whenever accounts change, to trigger a refetch.
  refreshKey?: number;
}

// Resolve whether a Plaid-bank target is connected by matching its institution
// name aliases against the names of the user's loaded accounts.
function isPlaidTargetConnected(
  target: ConnectionTargetStatus,
  accounts: PlaidAccount[]
): boolean {
  if (!target.matchInstitutionNames?.length) return false;
  const names = accounts
    .map((a) => a.institution?.name?.toLowerCase() || "")
    .filter(Boolean);
  return target.matchInstitutionNames.some((alias) =>
    names.some((n) => n.includes(alias.toLowerCase()))
  );
}

const ConnectChecklist: React.FC<ConnectChecklistProps> = ({
  accounts,
  onConnectBank,
  onConnectStripe,
  onAddWallet,
  refreshKey,
}) => {
  const [status, setStatus] = useState<ConnectionStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    plaidService
      .getConnectionStatus()
      .then((res) => {
        if (!cancelled) {
          setStatus(res);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load status");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // Merge server truth with client-resolved Plaid-bank state.
  const targets = useMemo(() => {
    if (!status) return [];
    return status.targets.map((t) => {
      if (t.method === "plaid_link" && !t.connected) {
        const connected = isPlaidTargetConnected(t, accounts);
        return connected ? { ...t, connected: true, nextAction: null } : t;
      }
      return t;
    });
  }, [status, accounts]);

  const connectedCount = targets.filter((t) => t.connected).length;
  const pending = targets.filter((t) => !t.connected);
  const pct = targets.length
    ? Math.round((connectedCount / targets.length) * 100)
    : 0;

  const ctaFor = useCallback(
    (t: ConnectionTargetStatus) => {
      if (t.method === "plaid_link")
        return { label: "Connect Bank", onClick: onConnectBank };
      if (t.method === "stripe_oauth")
        return { label: "Connect Stripe", onClick: onConnectStripe };
      if (t.method === "manual_csv" && t.provider)
        return {
          label: "Add Wallet",
          onClick: () =>
            onAddWallet(
              t.provider as "cash_app" | "venmo" | "lyft_direct" | "uber_card"
            ),
        };
      return null;
    },
    [onConnectBank, onConnectStripe, onAddWallet]
  );

  // Build a copyable prompt the user hands to their own AI agent (which speaks
  // to the Focus: Finance MCP server) to finish the pending connections.
  const agentPrompt = useMemo(() => {
    if (!pending.length) return "";
    const steps = pending.map((t) => ({
      target: t.label,
      method: t.method,
      mcpTool: t.nextAction?.mcpTool || null,
      provider: t.provider,
      instructions: t.nextAction?.instructions || "",
      csvSource: t.nextAction?.csvSource || null,
    }));
    const lines = [
      "You have access to the Focus: Finance MCP server (tools: connection_status, list_accounts, create_manual_wallet, import_wallet_csv).",
      "Help me connect my remaining financial accounts. For each pending item below:",
      "- Bank (plaid_link) / Stripe (stripe_oauth): these need a browser login, so give me the exact steps and the link to open.",
      "- Wallet (manual_csv): call create_manual_wallet with the provider, then help me export the CSV and call import_wallet_csv.",
      "",
      "Pending accounts:",
      ...pending.map(
        (t, i) =>
          `${i + 1}. ${t.label} — ${t.method}${
            t.nextAction?.instructions ? ` — ${t.nextAction.instructions}` : ""
          }`
      ),
      "",
      "Machine-readable steps:",
      "```json",
      JSON.stringify(steps, null, 2),
      "```",
    ];
    return lines.join("\n");
  }, [pending]);

  const copyPrompt = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(agentPrompt);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [agentPrompt]);

  // Nothing to show if everything is connected and there was no error.
  if (!loading && !error && pending.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.25,
          cursor: "pointer",
          bgcolor: "action.hover",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ flexShrink: 0 }}
        >
          Connect your accounts
        </Typography>
        {status && (
          <Chip
            size="small"
            color={pending.length === 0 ? "success" : "primary"}
            label={`${connectedCount} of ${targets.length} connected`}
          />
        )}
        <Box sx={{ flexGrow: 1, minWidth: 80 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        {loading && <LinearProgress />}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {status && (
          <>
            {pending.length > 0 && (
              <Box sx={{ px: 2, pt: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SmartToyIcon />}
                  endIcon={<ContentCopyIcon />}
                  onClick={copyPrompt}
                >
                  Hand this to my AI agent
                </Button>
                <Tooltip title="Copies a prompt your own AI agent can run against the Focus: Finance MCP server to finish these connections.">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1.5 }}
                  >
                    Uses the Focus: Finance MCP server
                  </Typography>
                </Tooltip>
              </Box>
            )}
            <List dense>
              {targets.map((t) => {
                const cta = t.connected ? null : ctaFor(t);
                return (
                  <ListItem
                    key={t.id}
                    secondaryAction={
                      cta ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={cta.onClick}
                        >
                          {cta.label}
                        </Button>
                      ) : (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={
                            t.accountCount > 0
                              ? `${t.accountCount} account${
                                  t.accountCount === 1 ? "" : "s"
                                }`
                              : "Connected"
                          }
                        />
                      )
                    }
                  >
                    <Box sx={{ mr: 1.5, display: "flex" }}>
                      {t.connected ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <RadioButtonUncheckedIcon
                          color="disabled"
                          fontSize="small"
                        />
                      )}
                    </Box>
                    <ListItemText
                      primary={t.label}
                      secondary={
                        t.connected
                          ? null
                          : t.nextAction?.instructions || undefined
                      }
                      secondaryTypographyProps={{
                        sx: { pr: 10 },
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </Collapse>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Agent prompt copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Paper>
  );
};

export default ConnectChecklist;
